import {uniq, omit} from 'lodash';

import {PageAccess} from 'insightvm-ui-nav-menus';

import AuthenticationType from 'resources/AuthenticationType';
import ConsoleVersionByReleaseNote from 'resources/ConsoleVersionByReleaseNote';
import PreviewFeature from 'resources/PreviewFeature';
import ProductEdition from 'resources/ProductEdition';
import {UserPrivilege} from 'resources/UserPrivilege';
import {Feature} from 'types/feature';
import {isCurrentVersionAtLeast} from 'utils/compareSemver';
import {isFeatureDisabled} from 'utils/feature';

import {isFedRamp} from './isFedRamp';

const getRestrictedPrivileges = ({
  agentHasData,
  pairingKey,
  isAwsMember,
  isConsoleOwned,
  user,
}) => {
  if (user.isDisabled) {
    return [UserPrivilege.CONSOLE_DISABLED];
  }

  if (pairingKey) {
    return [UserPrivilege.CONSOLE_PAIRING];
  }

  if (user.isProjectOnly) {
    return [UserPrivilege.REMEDIATION];
  }

  if (isAwsMember && !user.membership.isActive) {
    return [UserPrivilege.AWS_EXPIRED];
  }

  if (isAwsMember && !agentHasData && user.membership.isActive) {
    return [UserPrivilege.AWS];
  }

  if (isConsoleOwned && !user.membership.isActive) {
    return [UserPrivilege.CONSOLE_EXPIRED];
  }

  return [];
};

export default ({
  agentHasData,
  consoleUrl,
  consoleVersion,
  isAgentOnly,
  isAwsMember,
  isConsoleOwned,
  isDev,
  isInternalOrg,
  orgConsoleUrl,
  pairingKey,
  user,
}) => {
  const restrictedPrivileges = getRestrictedPrivileges({
    agentHasData,
    isAwsMember,
    isConsoleOwned,
    pairingKey,
    user,
  });

  if (restrictedPrivileges.length) {
    let pageAccess = PageAccess.SINGLE_NO_USER;

    if (restrictedPrivileges.includes(UserPrivilege.CONSOLE_DISABLED)) {
      pageAccess = PageAccess.RESTRICTED_USER;
    } else if (user.consoleSessionId || user.ipimsSessionId) {
      pageAccess = PageAccess.SINGLE;
    }

    return {
      pageAccess,
      privileges: restrictedPrivileges,
    };
  }

  let privileges = [
    ...(user.privileges || []),
    UserPrivilege.AGENTS,
    UserPrivilege.ASSET,
    UserPrivilege.DASHBOARD,
    UserPrivilege.MANAGEMENT,
    UserPrivilege.REPORTS,
    UserPrivilege.VULNERABILITY,
    UserPrivilege.POLICY,
  ];

  //these privileges give access to the Administator page
  const adminPagePrivs = [
    UserPrivilege.CONSOLE.MANAGE_SITES,
    UserPrivilege.CONSOLE.MANAGE_SCAN_ENGINES,
    UserPrivilege.CONSOLE.MANAGE_SCAN_TEMPLATES,
    UserPrivilege.CONSOLE.CONFIGURE_GLOBAL_SETTINGS,
    UserPrivilege.CONSOLE.MANAGE_STATIC_ASSET_GROUPS,
    UserPrivilege.CONSOLE.MANAGE_DYNAMIC_ASSET_GROUPS,
    UserPrivilege.CONSOLE.APPROVE_VULN_EXCEPTIONS,
    UserPrivilege.CONSOLE.DELETE_VULN_EXCEPTIONS,
    UserPrivilege.CONSOLE.MANAGE_VULN_INVESTIGATIONS,
  ];

  if (privileges.includes(UserPrivilege.ALL_PRIVILEGES)) {
    if (consoleUrl) {
      for (let [, value] of Object.entries(
        omit(UserPrivilege.CONSOLE, [
          UserPrivilege.CONSOLE.TICKETS,
          UserPrivilege.CONSOLE.POLICIES,
          UserPrivilege.CONSOLE.ALL_POLICIES,
        ])
      ))
        privileges.push(value);

      privileges.push(
        user.productEdition === ProductEdition.INSIGHT_VM
          ? UserPrivilege.CONSOLE.ALL_POLICIES
          : UserPrivilege.CONSOLE.POLICIES
      );
    }
    if (user.productEdition === ProductEdition.INSIGHT_VM) {
      privileges.push(UserPrivilege.REMEDIATION);
      privileges.push(UserPrivilege.AGENT_POLICY);
    }
  } else if (consoleUrl) {
    privileges.push(
      UserPrivilege.CONSOLE.HOME,
      UserPrivilege.CONSOLE.ASSET,
      user.productEdition === ProductEdition.INSIGHT_VM
        ? UserPrivilege.CONSOLE.ALL_POLICIES
        : UserPrivilege.CONSOLE.POLICIES,
      UserPrivilege.CONSOLE.VULNERABILITY,
      UserPrivilege.CONSOLE.REPORTS
    );

    if (privileges.some((privilege) => adminPagePrivs.includes(privilege)))
      privileges.push(UserPrivilege.CONSOLE.ADMIN);

    if (privileges.includes(UserPrivilege.CONSOLE.MANAGE_REPORTS))
      privileges.push(UserPrivilege.CONSOLE.CREATE_REPORTS);
  }

  if (privileges.includes(UserPrivilege.CONSOLE.CREATE_TICKETS) || isDev)
    privileges.push(UserPrivilege.REMEDIATION);

  if (user.productEdition === ProductEdition.INSIGHT_VM) {
    privileges.push(UserPrivilege.GOALS);

    if (!isFeatureDisabled(user, Feature.CCA) && !isFedRamp()) {
      privileges.push(UserPrivilege.INFRASTRUCTURE);
    }

    if (!isFeatureDisabled(user, Feature.CONTAINER)) {
      privileges.push(UserPrivilege.CONTAINER);
    }
  }

  if (user.productEdition === ProductEdition.NEXPOSE_ENTERPRISE) {
    privileges.push(UserPrivilege.CONSOLE.TICKETS);
  }

  if (
    (!isInternalOrg &&
      !isAgentOnly &&
      user.productEdition === ProductEdition.INSIGHT_VM &&
      (user.isAdmin ||
        user.previewFeatures.includes(PreviewFeature.AUTOMATION_ALL_USERS))) ||
    isDev
  ) {
    privileges.push(UserPrivilege.AUTOMATION);
  }

  if (
    privileges.includes(UserPrivilege.AUTOMATION) &&
    user.hasPlatformAccount
  ) {
    privileges.push(UserPrivilege.AUTOMATION_WRITE);
  }
  if (isAwsMember) {
    privileges.push(UserPrivilege.AWS);
  }

  if (isDev) {
    privileges.push(UserPrivilege.AGENT_POLICY);
  }

  const isConsoleSSOCapableForThirdpartyUsers = isCurrentVersionAtLeast(
    consoleVersion,
    ConsoleVersionByReleaseNote.SSO_FOR_THIRDPARTY_AUTH_USERS
  );
  const isFirstConsoleUser = user.userKey.endsWith('user-1');
  const isNonAdminOnSSOEnabledOrg = orgConsoleUrl && !user.isAdmin;
  const isLocallyAuthSourcedAdmin =
    user.isAdmin &&
    [AuthenticationType.DATA_STORE, AuthenticationType.XML].includes(
      user.authSource
    );
  const isConsoleSSOCapableForAdminOnly =
    !isConsoleSSOCapableForThirdpartyUsers && isLocallyAuthSourcedAdmin;
  const isConsoleSSOCapableForAllUsers =
    isConsoleSSOCapableForThirdpartyUsers &&
    (user.isAdmin || isNonAdminOnSSOEnabledOrg);
  const userCanEnableSSO =
    !isFirstConsoleUser &&
    user.isPlatformAccountActive &&
    !user.isSSOEnabled &&
    !user.isPlatformOnly &&
    (isConsoleSSOCapableForAllUsers || isConsoleSSOCapableForAdminOnly) &&
    !user.previewFeatures.includes(PreviewFeature.EXCLUDE_IVM_PLATFORM_LOGIN);

  if (userCanEnableSSO || user.isSSOEnabled) {
    privileges.push(UserPrivilege.PLATFORM_LOGIN);
  }

  if (
    user.previewFeatures.includes(PreviewFeature.EXCLUDE_IPIMS_FOR_ALL_USERS)
  ) {
    privileges.push(UserPrivilege.EXCLUDE_IPIMS_FOR_ALL_USERS);
  }

  return {
    pageAccess: PageAccess.ALL,
    privileges: uniq(privileges).sort(),
  };
};
